import React, { useEffect, useState } from 'react'
import { IoChevronBack, IoNotificationsOutline, IoPeopleOutline, IoFootball, IoSquare, IoChevronDown, IoTrophy, IoEye } from 'react-icons/io5'
import { GiRunningShoe, GiSoccerBall } from 'react-icons/gi'
// Radar chart removed - percentiles data not available
import type { PlayerProfile, TeamLite } from '../types'
import {
  getExtendedPlayerProfile,
  getPlayerMatches,
  getPlayerStats,
  getPlayerCareer,
  getPlayerRealtimeStats,
  type PlayerMatch,
  type UpcomingFixture,
  type PlayerSeasonStats,
  type CareerSeasonEntry,
  type CareerClubEntry,
  type RealTimeLeagueStats,
  type RealTimeStatsResponse,
  type PlayerBirth,
} from '../api'
import { OfflineBadge, StaleIndicator } from './StaleIndicator'
import { getCachedData, setCachedData } from '../utils/offlineCache'
import { isOnline, type ResponseMeta, type SectionMeta } from '../utils/dataFreshness'

// Extended types for the full player profile page
export type PlayerInjury = {
  injury_type: string
  injury_reason?: string
  start_date: string
  expected_return?: string | null
}

export type PlayerTrophy = {
  trophy_name: string
  trophy_type: string // league, cup, international, individual
  season: string
  team_id?: number
  team_name?: string
  team_crest?: string
  country?: string
}

export type PlayerTransfer = {
  from_team?: TeamLite
  to_team?: TeamLite
  transfer_date: string
  transfer_type: string
  fee_amount?: number
}

export type PlayerMarketValue = {
  value_date: string
  market_value: number
  team_id?: number
  team_crest?: string
}

export type PlayerPercentiles = {
  goals_percentile?: number
  assists_percentile?: number
  rating_percentile?: number
  minutes_percentile?: number
  passes_percentile?: number
  key_passes_percentile?: number
  tackles_percentile?: number
  interceptions_percentile?: number
  duels_won_percentile?: number
  dribbles_percentile?: number
  shots_percentile?: number
  touches_percentile?: number
  chances_created_percentile?: number
  aerial_duels_percentile?: number
  defensive_contributions_percentile?: number
  cohort_size?: number
}

export type ExtendedPlayerProfile = PlayerProfile & {
  height?: number | null
  weight?: number | null
  preferred_foot?: string | null
  market_value?: number | null
  injury?: PlayerInjury | null
  trophies?: PlayerTrophy[]
  transfers?: PlayerTransfer[]
  market_value_history?: PlayerMarketValue[]
  percentiles?: PlayerPercentiles | null
  primary_position?: string | null
  secondary_positions?: string[]
  league_name?: string | null
  league_logo?: string | null
  season?: string | null
  // Staleness metadata from API
  _meta?: ResponseMeta
}

interface PlayerProfilePageProps {
  playerId: number
  onClose: () => void
}

type TabType = 'Profile' | 'Matches' | 'Stats' | 'Career'

/**
 * Full-featured Player Profile Page matching FotMob design
 * Shows: injury alert, bio grid, league stats, radar chart, position map, value chart, trophies
 */
export function PlayerProfilePage({ playerId, onClose }: PlayerProfilePageProps) {
  const [player, setPlayer] = useState<ExtendedPlayerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('Profile')
  const [isOffline, setIsOffline] = useState(!isOnline())
  const [usingCachedData, setUsingCachedData] = useState(false)

  // Matches tab state
  const [matches, setMatches] = useState<PlayerMatch[]>([])
  const [upcomingFixtures, setUpcomingFixtures] = useState<UpcomingFixture[]>([])
  const [matchesLoading, setMatchesLoading] = useState(false)
  const [matchesLoaded, setMatchesLoaded] = useState(false)

  // Stats tab state (also used for Profile tab league stats)
  const [seasonStats, setSeasonStats] = useState<PlayerSeasonStats[]>([])
  const [realtimeLeagueStats, setRealtimeLeagueStats] = useState<RealTimeLeagueStats[]>([])
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsLoaded, setStatsLoaded] = useState(false)
  const [selectedSeasonStats, setSelectedSeasonStats] = useState<PlayerSeasonStats | null>(null)
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false)
  const [profileLeagueStats, setProfileLeagueStats] = useState<PlayerSeasonStats | null>(null)
  const [usingRealtimeStats, setUsingRealtimeStats] = useState(false)

  // Extended bio data from real-time API
  const [realtimeBio, setRealtimeBio] = useState<{
    firstname?: string | null
    lastname?: string | null
    birth?: PlayerBirth | null
    height?: string | null
    weight?: string | null
  } | null>(null)

  // Career tab state
  const [clubSeasons, setClubSeasons] = useState<CareerSeasonEntry[]>([])
  const [careerClubs, setCareerClubs] = useState<CareerClubEntry[]>([])
  const [nationalSeasons, setNationalSeasons] = useState<CareerSeasonEntry[]>([])
  const [nationalTeams, setNationalTeams] = useState<CareerClubEntry[]>([])
  const [careerLoading, setCareerLoading] = useState(false)
  const [careerLoaded, setCareerLoaded] = useState(false)
  const [careerView, setCareerView] = useState<'Seasons' | 'Club'>('Seasons')
  const [showAllSeasons, setShowAllSeasons] = useState(false)
  const INITIAL_SEASONS_LIMIT = 3

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Load Matches tab data when tab is selected
  useEffect(() => {
    if (activeTab !== 'Matches' || matchesLoaded || matchesLoading) return

    setMatchesLoading(true)
    getPlayerMatches(playerId, 30)
      .then((response) => {
        setMatches(response.matches)
        setUpcomingFixtures(response.upcoming)
        setMatchesLoaded(true)
      })
      .catch((err) => {
        console.error('Failed to load matches:', err)
      })
      .finally(() => {
        setMatchesLoading(false)
      })
  }, [activeTab, playerId, matchesLoaded, matchesLoading])

  // Load Stats data - HYBRID APPROACH
  // Try real-time API-Football first (1 API call), fall back to cached data
  useEffect(() => {
    if (statsLoaded || statsLoading) return

    setStatsLoading(true)

    // Helper to convert RealTimeLeagueStats to PlayerSeasonStats format
    const convertRealtimeToSeasonStats = (
      leagues: RealTimeLeagueStats[],
      season: number
    ): PlayerSeasonStats[] => {
      return leagues.map((league) => ({
        season: `${season}/${season + 1}`,
        season_year: season,
        league_id: league.league_id,
        league_name: league.league_name,
        league_country: league.league_country,
        team_id: league.team_id || 0,
        team_name: league.team_name || '',
        team_crest: league.team_crest || '',
        matches: league.matches,
        started: league.started,
        minutes: league.minutes,
        goals: league.goals,
        assists: league.assists,
        rating: league.rating,
        shots: league.shots,
        shots_on_target: league.shots_on_target,
        passes: league.passes,
        key_passes: league.key_passes,
        pass_accuracy: league.pass_accuracy,
        tackles: league.tackles,
        interceptions: league.interceptions,
        blocks: league.blocks,
        clearances: 0,
        duels_total: league.duels_total,
        duels_won: league.duels_won,
        aerials_total: 0,
        aerials_won: 0,
        dribbles_attempts: league.dribbles_attempts,
        dribbles_success: league.dribbles_success,
        dribbles_past: league.dribbles_past,
        yellows: league.yellows,
        reds: league.reds,
        yellow_red: league.yellow_red,
        fouls_committed: league.fouls_committed,
        fouls_drawn: league.fouls_drawn,
        saves: league.saves,
        goals_conceded: league.goals_conceded,
        penalty_saved: league.penalty_saved,
        penalty_won: league.penalty_won,
        penalty_committed: league.penalty_committed,
        penalty_scored: league.penalty_scored,
        penalty_missed: league.penalty_missed,
        position: league.position,
        captain: league.captain ? 1 : 0,
        sub_in: league.sub_in,
        sub_out: league.sub_out,
        bench: league.bench,
      }))
    }

    const setStatsFromSeasons = (seasons: PlayerSeasonStats[], isRealtime: boolean) => {
      setSeasonStats(seasons)
      setUsingRealtimeStats(isRealtime)
      if (seasons.length > 0) {
        setSelectedSeasonStats(seasons[0])
        // Set primary league stats for Profile tab (first non-cup competition)
        const primaryLeague = seasons.find((s) => {
          const name = (s.league_name || '').toLowerCase()
          return !name.includes('cup') && !name.includes('champions') && !name.includes('europa')
        }) || seasons[0]
        setProfileLeagueStats(primaryLeague)
      }
      setStatsLoaded(true)
    }

    // Try real-time first, then fall back to cached
    getPlayerRealtimeStats(playerId)
      .then((response) => {
        const seasons = convertRealtimeToSeasonStats(response.leagues, response.season)
        setStatsFromSeasons(seasons, true)
        // Store full realtime league stats for detailed view
        setRealtimeLeagueStats(response.leagues)
        // Update player with fresh nationality/age from API-Football if available
        if (response.nationality || response.age) {
          setPlayer((prev) => prev ? {
            ...prev,
            nationality: response.nationality || prev.nationality,
            age: response.age ?? prev.age,
          } : prev)
        }
        // Store extended bio data
        setRealtimeBio({
          firstname: response.firstname,
          lastname: response.lastname,
          birth: response.birth,
          height: response.height,
          weight: response.weight,
        })
        console.log('[PlayerProfile] Using real-time stats from API-Football')
      })
      .catch((err) => {
        console.warn('[PlayerProfile] Real-time fetch failed, falling back to cached:', err.message)
        // Fall back to cached/aggregated stats
        return getPlayerStats(playerId)
          .then((response) => {
            setStatsFromSeasons(response.seasons, false)
            console.log('[PlayerProfile] Using cached stats')
          })
      })
      .catch((err) => {
        console.error('Failed to load stats:', err)
      })
      .finally(() => {
        setStatsLoading(false)
      })
  }, [playerId, statsLoaded, statsLoading])

  // Load Career tab data when tab is selected
  useEffect(() => {
    if (activeTab !== 'Career' || careerLoaded || careerLoading) return

    setCareerLoading(true)
    getPlayerCareer(playerId)
      .then((response) => {
        setClubSeasons(response.club_seasons)
        setCareerClubs(response.clubs)
        setNationalSeasons(response.national_seasons)
        setNationalTeams(response.national_teams)
        setCareerLoaded(true)
      })
      .catch((err) => {
        console.error('Failed to load career:', err)
      })
      .finally(() => {
        setCareerLoading(false)
      })
  }, [activeTab, playerId, careerLoaded, careerLoading])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setUsingCachedData(false)

    // TODO: Get userId from auth context (Clerk) for view tracking
    const userId = undefined

    // Track fetch start time for minimum skeleton display (200ms)
    const fetchStartTime = Date.now()
    const MIN_SKELETON_TIME = 200

    const cacheKey = `player_extended_${playerId}`

    // Helper to set data with minimum skeleton time
    const setDataWithDelay = (data: ExtendedPlayerProfile, fromCache: boolean) => {
      const elapsed = Date.now() - fetchStartTime
      const remainingTime = Math.max(0, MIN_SKELETON_TIME - elapsed)

      const applyData = () => {
        if (!cancelled) {
          setPlayer(data)
          setUsingCachedData(fromCache)
          setLoading(false)
          // NOTE: Tabs are now loaded on-demand when user clicks them
          // This makes the initial profile load instant
        }
      }

      if (remainingTime > 0) {
        setTimeout(applyData, remainingTime)
      } else {
        applyData()
      }
    }

    // Helper to handle errors with cache fallback
    const handleErrorWithCache = async (err: Error) => {
      if (cancelled) return

      // Try to load from cache on error (stale-while-revalidate)
      const cached = await getCachedData<ExtendedPlayerProfile>(cacheKey)
      if (cached && !cancelled) {
        setDataWithDelay(cached.data, true)
        return
      }

      // No cache available, show error
      const elapsed = Date.now() - fetchStartTime
      const remainingTime = Math.max(0, MIN_SKELETON_TIME - elapsed)

      const applyError = () => {
        if (!cancelled) {
          setError(err.message || 'Failed to load player')
          setLoading(false)
        }
      }

      if (remainingTime > 0) {
        setTimeout(applyError, remainingTime)
      } else {
        applyError()
      }
    }

    // If offline, try cache first
    if (!isOnline()) {
      getCachedData<ExtendedPlayerProfile>(cacheKey).then((cached) => {
        if (cached && !cancelled) {
          setDataWithDelay(cached.data, true)
        } else if (!cancelled) {
          setError('You\'re offline and no cached data is available')
          setLoading(false)
        }
      })
      return () => { cancelled = true }
    }

    // Fetch from API
    getExtendedPlayerProfile(playerId, userId)
      .then((data) => {
        if (cancelled) return

        // Cache the data for offline use
        setCachedData(cacheKey, data)

        setDataWithDelay(data, false)
      })
      .catch(handleErrorWithCache)

    return () => {
      cancelled = true
    }
  }, [playerId])

  // Format stat value with fallback
  const fmt = (value: number | undefined | null, decimals = 0): string => {
    if (value === undefined || value === null) return '—'
    return decimals > 0 ? value.toFixed(decimals) : String(Math.round(value))
  }

  // Format currency
  const fmtValue = (value: number | undefined | null): string => {
    if (!value) return '—'
    if (value >= 1000000) return `£${(value / 1000000).toFixed(0)}M`
    if (value >= 1000) return `£${(value / 1000).toFixed(0)}K`
    return `£${value}`
  }

  // Format date
  const fmtDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  // Get position label
  const getPositionLabel = (position?: string | null): string => {
    if (!position) return 'Unknown'
    const pos = position.toUpperCase()
    if (pos.includes('GOALKEEPER') || pos.includes('GK') || pos === 'G') return 'Goalkeeper'
    if (pos.includes('DEFENDER') || pos.includes('DEF') || pos.includes('CB') || pos.includes('LB') || pos.includes('RB')) return 'Defender'
    if (pos.includes('MIDFIELDER') || pos.includes('MID') || pos.includes('CM') || pos.includes('DM') || pos.includes('AM')) return 'Midfielder'
    if (pos.includes('FORWARD') || pos.includes('FWD') || pos.includes('ST') || pos.includes('LW') || pos.includes('RW')) return 'Forward'
    return position
  }

  // Get short position code for pitch visualization
  const getPositionCode = (position?: string | null): string => {
    if (!position) return '?'
    const pos = position.toUpperCase()
    if (pos.includes('STRIKER') || pos === 'ST' || pos === 'CF') return 'ST'
    if (pos.includes('LEFT WINGER') || pos === 'LW') return 'LW'
    if (pos.includes('RIGHT WINGER') || pos === 'RW') return 'RW'
    if (pos.includes('ATTACKING MID') || pos === 'AM' || pos === 'CAM') return 'AM'
    if (pos.includes('CENTRAL MID') || pos === 'CM') return 'CM'
    if (pos.includes('DEFENSIVE MID') || pos === 'DM' || pos === 'CDM') return 'DM'
    if (pos.includes('LEFT BACK') || pos === 'LB') return 'LB'
    if (pos.includes('RIGHT BACK') || pos === 'RB') return 'RB'
    if (pos.includes('CENTER BACK') || pos === 'CB') return 'CB'
    if (pos.includes('GOALKEEPER') || pos === 'GK' || pos === 'G') return 'GK'
    if (pos.includes('DEFENDER')) return 'DEF'
    if (pos.includes('MIDFIELDER')) return 'MID'
    if (pos.includes('FORWARD') || pos.includes('ATTACKER')) return 'FWD'
    return pos.slice(0, 3)
  }

  // Get position coordinates on pitch (percentage-based)
  const getPositionCoords = (posCode: string): { x: number; y: number } => {
    const positions: Record<string, { x: number; y: number }> = {
      'GK': { x: 50, y: 92 },
      'CB': { x: 50, y: 75 },
      'LB': { x: 15, y: 70 },
      'RB': { x: 85, y: 70 },
      'DEF': { x: 50, y: 75 },
      'DM': { x: 50, y: 60 },
      'CDM': { x: 50, y: 60 },
      'CM': { x: 50, y: 50 },
      'LM': { x: 15, y: 50 },
      'RM': { x: 85, y: 50 },
      'MID': { x: 50, y: 50 },
      'AM': { x: 50, y: 35 },
      'CAM': { x: 50, y: 35 },
      'LW': { x: 20, y: 25 },
      'RW': { x: 80, y: 25 },
      'ST': { x: 50, y: 15 },
      'CF': { x: 50, y: 20 },
      'FWD': { x: 50, y: 18 },
    }
    return positions[posCode] || { x: 50, y: 50 }
  }

  // Get country flag emoji (simplified - in real app use flag images)
  const getCountryCode = (nationality?: string | null): string => {
    if (!nationality) return ''
    const codes: Record<string, string> = {
      'France': 'FRA',
      'England': 'ENG',
      'Spain': 'ESP',
      'Germany': 'GER',
      'Brazil': 'BRA',
      'Argentina': 'ARG',
      'Portugal': 'POR',
      'Italy': 'ITA',
      'Netherlands': 'NED',
      'Belgium': 'BEL',
    }
    return codes[nationality] || nationality.slice(0, 3).toUpperCase()
  }

  // Get country flag emoji
  const getCountryFlag = (country?: string | null): string => {
    if (!country) return ''
    const flags: Record<string, string> = {
      // Main European countries
      'France': '🇫🇷',
      'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      'Spain': '🇪🇸',
      'Germany': '🇩🇪',
      'Italy': '🇮🇹',
      'Portugal': '🇵🇹',
      'Netherlands': '🇳🇱',
      'Belgium': '🇧🇪',
      'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      'Wales': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
      'Norway': '🇳🇴',
      'Sweden': '🇸🇪',
      'Denmark': '🇩🇰',
      'Austria': '🇦🇹',
      'Switzerland': '🇨🇭',
      'Poland': '🇵🇱',
      'Ukraine': '🇺🇦',
      'Turkey': '🇹🇷',
      'Greece': '🇬🇷',
      'Croatia': '🇭🇷',
      'Serbia': '🇷🇸',
      'Czech-Republic': '🇨🇿',
      // South America
      'Brazil': '🇧🇷',
      'Argentina': '🇦🇷',
      'Colombia': '🇨🇴',
      'Uruguay': '🇺🇾',
      'Chile': '🇨🇱',
      // Other
      'USA': '🇺🇸',
      'Mexico': '🇲🇽',
      'Japan': '🇯🇵',
      'South-Korea': '🇰🇷',
      'Australia': '🇦🇺',
      'Morocco': '🇲🇦',
      'Senegal': '🇸🇳',
      'Nigeria': '🇳🇬',
      'Ghana': '🇬🇭',
      'Ivory Coast': '🇨🇮',
      'Cote D\'Ivoire': '🇨🇮',
      'Cameroon': '🇨🇲',
      'Algeria': '🇩🇿',
      'Tunisia': '🇹🇳',
      'Egypt': '🇪🇬',
      'South Africa': '🇿🇦',
      'Saudi-Arabia': '🇸🇦',
      // International/World
      'World': '🌍',
      'Europe': '🇪🇺',
    }
    return flags[country] || '🏳️'
  }

  // Use player data directly from API
  const playerData = player

  // Group trophies by team
  const groupTrophiesByTeam = (trophies: PlayerTrophy[] | undefined) => {
    if (!trophies) return []
    const grouped: Record<string, { team_name: string; country: string; trophies: Record<string, string[]> }> = {}

    trophies.forEach((trophy) => {
      const teamKey = trophy.team_name || 'Unknown'
      if (!grouped[teamKey]) {
        grouped[teamKey] = {
          team_name: teamKey,
          country: trophy.country || '',
          trophies: {},
        }
      }
      if (!grouped[teamKey].trophies[trophy.trophy_name]) {
        grouped[teamKey].trophies[trophy.trophy_name] = []
      }
      grouped[teamKey].trophies[trophy.trophy_name].push(trophy.season)
    })

    return Object.values(grouped)
  }

  const tabs: TabType[] = ['Profile', 'Matches', 'Stats', 'Career']

  return (
    <div className="player-profile-overlay" onClick={onClose}>
      <div className="player-profile-page" onClick={(e) => e.stopPropagation()}>
        {/* Header - Top Bar */}
        <div className="player-profile-topbar">
          <button className="player-profile-back" onClick={onClose} aria-label="Back">
            <IoChevronBack size={24} />
          </button>

          <div className="player-profile-actions">
            <button className="player-profile-action-btn" aria-label="Compare">
              <IoPeopleOutline size={20} />
            </button>
            <button className="player-profile-action-btn" aria-label="Notifications">
              <IoNotificationsOutline size={20} />
            </button>
            <button className="player-profile-follow-btn">Follow</button>
          </div>
        </div>

        {/* Player Info Section */}
        {!loading && playerData && (
          <div className="player-profile-hero">
            {playerData.photo_url ? (
              <img src={playerData.photo_url} alt={playerData.name} className="player-profile-avatar" />
            ) : (
              <div className="player-profile-avatar-placeholder" />
            )}
            <div className="player-profile-hero-text">
              <h1 className="player-profile-name">{playerData.name}</h1>
              <div className="player-profile-team-row">
                {playerData.current_team?.crest_url && (
                  <img src={playerData.current_team.crest_url} alt="" className="player-profile-team-crest" />
                )}
                <span className="player-profile-team-name">{playerData.current_team?.name}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="player-profile-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`player-profile-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="player-profile-content">
          {/* Offline Banner */}
          {isOffline && <OfflineBadge />}

          {/* Cached Data Indicator */}
          {usingCachedData && !isOffline && (
            <div className="player-profile-cached-notice">
              <StaleIndicator
                meta={{ is_stale: true, data_age: 'from cache' }}
              />
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="player-profile-loading">
              <PlayerProfileSkeleton />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="player-profile-error">
              <p>{error}</p>
            </div>
          )}

          {/* Profile Tab Content */}
          {!loading && !error && playerData && activeTab === 'Profile' && (
            <div className="player-profile-sections">
              {/* Injury Alert */}
              {playerData.injury && (
                <div className="player-profile-injury-card">
                  <div className="player-profile-injury-icon">
                    <span className="injury-cross">+</span>
                  </div>
                  <div className="player-profile-injury-info">
                    <span className="player-profile-injury-type">
                      {playerData.injury.injury_type} ({fmtDate(playerData.injury.start_date)})
                    </span>
                    <span className="player-profile-injury-return">
                      Expected return: {playerData.injury.expected_return ? fmtDate(playerData.injury.expected_return) : 'Unknown'}
                    </span>
                  </div>
                </div>
              )}

              {/* Bio Grid Card - only show fields with real data */}
              <div className="player-profile-card">
                <div className="player-profile-bio-grid bio-grid-compact">
                  <div className="player-profile-bio-item">
                    <span className="bio-value">{playerData.age || '—'} years</span>
                    <span className="bio-sublabel">{realtimeBio?.birth?.date || playerData.dob ? fmtDate(realtimeBio?.birth?.date || playerData.dob || '') : ''}</span>
                    <span className="bio-label">Age</span>
                  </div>
                  <div className="player-profile-bio-item">
                    <span className="bio-value bio-country">
                      <span className="country-flag">{getCountryFlag(playerData.nationality)}</span>
                      {playerData.nationality || '—'}
                    </span>
                    <span className="bio-label">Country</span>
                  </div>
                  <div className="player-profile-bio-item">
                    <span className="bio-value">{playerData.shirt_number || '—'}</span>
                    <span className="bio-label">Shirt</span>
                  </div>
                  <div className="player-profile-bio-item">
                    <span className="bio-value">{getPositionLabel(playerData.position)}</span>
                    <span className="bio-label">Position</span>
                  </div>
                </div>
                {/* Extended bio row - height, weight, birthplace */}
                {(realtimeBio?.height || realtimeBio?.weight || realtimeBio?.birth?.place) && (
                  <div className="player-profile-bio-grid bio-grid-compact bio-grid-extended">
                    {realtimeBio?.height && (
                      <div className="player-profile-bio-item">
                        <span className="bio-value">
                          {realtimeBio.height.includes('cm') ? realtimeBio.height : `${realtimeBio.height} cm`}
                        </span>
                        <span className="bio-label">Height</span>
                      </div>
                    )}
                    {realtimeBio?.weight && (
                      <div className="player-profile-bio-item">
                        <span className="bio-value">
                          {realtimeBio.weight.includes('kg') ? realtimeBio.weight : `${realtimeBio.weight} kg`}
                        </span>
                        <span className="bio-label">Weight</span>
                      </div>
                    )}
                    {realtimeBio?.birth?.place && (
                      <div className="player-profile-bio-item">
                        <span className="bio-value">
                          {realtimeBio.birth.place}{realtimeBio.birth.country && realtimeBio.birth.country !== playerData.nationality ? `, ${realtimeBio.birth.country}` : ''}
                        </span>
                        <span className="bio-label">Birthplace</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* League Stats Card - Shows primary league stats only */}
              <div className="player-profile-card">
                <div className="player-profile-league-header">
                  {profileLeagueStats?.team_crest && (
                    <img src={profileLeagueStats.team_crest} alt="" className="league-logo" />
                  )}
                  <span className="league-name">
                    {profileLeagueStats?.league_name || playerData.league_name || 'League Stats'}
                    {profileLeagueStats?.season && ` ${profileLeagueStats.season}`}
                  </span>
                  {usingRealtimeStats && (
                    <span className="realtime-badge" title="Stats fetched live from API-Football">LIVE</span>
                  )}
                </div>
                {statsLoading ? (
                  <div className="player-profile-league-stats">
                    <div className="skeleton-line" style={{ width: 40, height: 28 }} />
                    <div className="skeleton-line" style={{ width: 40, height: 28 }} />
                    <div className="skeleton-line" style={{ width: 40, height: 28 }} />
                    <div className="skeleton-line" style={{ width: 40, height: 28 }} />
                  </div>
                ) : (
                  <div className="player-profile-league-stats">
                    <div className="league-stat">
                      <span className="league-stat-value">{profileLeagueStats?.matches ?? fmt(playerData.games)}</span>
                      <span className="league-stat-label">Matches</span>
                    </div>
                    <div className="league-stat">
                      <span className="league-stat-value">{profileLeagueStats?.goals ?? fmt(playerData.goals)}</span>
                      <span className="league-stat-label">Goals</span>
                    </div>
                    <div className="league-stat">
                      <span className="league-stat-value">{profileLeagueStats?.assists ?? fmt(playerData.assists)}</span>
                      <span className="league-stat-label">Assists</span>
                    </div>
                    <div className="league-stat">
                      <span className="league-stat-value rating-badge">
                        {profileLeagueStats?.rating?.toFixed(2) ?? playerData.rating?.toFixed(2) ?? '—'}
                      </span>
                      <span className="league-stat-label">Rating</span>
                    </div>
                  </div>
                )}

                {/* Show other competitions if available */}
                {seasonStats.length > 1 && (
                  <div className="player-profile-other-comps">
                    <div className="other-comps-header">Other Competitions</div>
                    <div className="other-comps-list">
                      {seasonStats
                        .filter(s => s !== profileLeagueStats)
                        // Filter out friendlies (not real competitions)
                        .filter(s => {
                          const name = (s.league_name || '').toLowerCase()
                          return !name.includes('friendlies')
                        })
                        .map((stats) => (
                        <div key={`${stats.league_id}-${stats.season_year}`} className="other-comp-row">
                          <span className="other-comp-name">
                            {stats.league_country && (
                              <span className="other-comp-flag">{getCountryFlag(stats.league_country)}</span>
                            )}
                            {stats.league_name}
                            {stats.league_country && (
                              <span className="other-comp-country">({stats.league_country})</span>
                            )}
                          </span>
                          <div className="other-comp-stats">
                            <span>{stats.matches} Apps</span>
                            <span>{stats.goals} G</span>
                            <span>{stats.assists} A</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Position Card */}
              <div className="player-profile-card">
                <div className="player-profile-section-header">
                  <h3>Position</h3>
                </div>
                <div className="player-profile-position-content">
                  <div className="position-text">
                    <div className="position-primary">
                      <span className="position-label">Primary</span>
                      <span className="position-value">{playerData.primary_position || getPositionLabel(playerData.position)}</span>
                    </div>
                    {playerData.secondary_positions && playerData.secondary_positions.length > 0 && (
                      <div className="position-secondary">
                        <span className="position-label">Others</span>
                        <span className="position-value">{playerData.secondary_positions.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  <div className="position-pitch">
                    <MiniPitch
                      primaryPosition={getPositionCode(playerData.primary_position || playerData.position)}
                      secondaryPositions={playerData.secondary_positions?.map(p => getPositionCode(p)) || []}
                    />
                  </div>
                </div>
              </div>


              {/* Trophies Section */}
              {playerData.trophies && playerData.trophies.length > 0 && (
                <div className="player-profile-card">
                  <div className="player-profile-section-header">
                    <h3>Trophies</h3>
                  </div>
                  <div className="player-profile-trophies">
                    {groupTrophiesByTeam(playerData.trophies).map((teamGroup, idx) => (
                      <div key={idx} className="trophy-team-group">
                        <div className="trophy-team-header">
                          <div className="trophy-team-badge">
                            {/* Team badge placeholder */}
                            <span className="trophy-team-icon">⚽</span>
                          </div>
                          <div className="trophy-team-info">
                            <span className="trophy-team-name">{teamGroup.team_name}</span>
                            <span className="trophy-team-country">{teamGroup.country}</span>
                          </div>
                        </div>
                        <div className="trophy-list">
                          {Object.entries(teamGroup.trophies).map(([trophyName, seasons]) => (
                            <div key={trophyName} className="trophy-item">
                              <span className="trophy-count">{seasons.length}</span>
                              <span className="trophy-icon">🏆</span>
                              <span className="trophy-name">{trophyName}</span>
                              <span className="trophy-seasons">({seasons.join(' · ')})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Matches Tab */}
          {activeTab === 'Matches' && !loading && (
            <div className="player-matches-tab">
              {matchesLoading && (
                <div className="player-matches-loading">
                  <div className="skeleton-card" style={{ height: 80 }} />
                  <div className="skeleton-card" style={{ height: 80 }} />
                  <div className="skeleton-card" style={{ height: 80 }} />
                </div>
              )}
              {!matchesLoading && (
                <>
                  {/* Upcoming Fixtures - reversed so next match is at bottom, adjacent to results */}
                  {upcomingFixtures.length > 0 && (
                    <div className="player-matches-section">
                      <h3 className="player-matches-section-title">Upcoming</h3>
                      <div className="player-matches-list">
                        {[...upcomingFixtures].reverse().map((fixture) => (
                          <UpcomingFixtureRow
                            key={fixture.fixture_id}
                            fixture={fixture}
                            onClick={() => {
                              window.history.pushState(null, '', `#match/${fixture.fixture_id}`)
                              window.dispatchEvent(new HashChangeEvent('hashchange'))
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Matches */}
                  {matches.length > 0 && (
                    <div className="player-matches-section">
                      <h3 className="player-matches-section-title">Results</h3>
                      <div className="player-matches-list">
                        {matches.map((match) => (
                          <PlayerMatchRow
                            key={match.fixture_id}
                            match={match}
                            onClick={() => {
                              window.history.pushState(null, '', `#match/${match.fixture_id}`)
                              window.dispatchEvent(new HashChangeEvent('hashchange'))
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {matches.length === 0 && upcomingFixtures.length === 0 && (
                    <div className="player-profile-placeholder">
                      <p>No match history available</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'Stats' && !loading && (
            <div className="player-stats-tab">
              {statsLoading && (
                <div className="player-stats-loading">
                  <div className="skeleton-card" style={{ height: 60 }} />
                  <div className="skeleton-card" style={{ height: 150 }} />
                  <div className="skeleton-card" style={{ height: 200 }} />
                </div>
              )}
              {!statsLoading && seasonStats.length === 0 && (
                <div className="player-profile-placeholder">
                  <p>No stats available</p>
                </div>
              )}
              {!statsLoading && seasonStats.length > 0 && selectedSeasonStats && (
                <>
                  {/* League/Season Selector */}
                  <div className="player-stats-selector" onClick={() => setShowLeagueDropdown(!showLeagueDropdown)}>
                    {selectedSeasonStats.team_crest && (
                      <img src={selectedSeasonStats.team_crest} alt="" className="stats-selector-crest" />
                    )}
                    <span className="stats-selector-text">
                      {selectedSeasonStats.league_name} {selectedSeasonStats.season}
                    </span>
                    <IoChevronDown size={16} className={`stats-selector-arrow ${showLeagueDropdown ? 'open' : ''}`} />
                  </div>

                  {/* Dropdown Menu */}
                  {showLeagueDropdown && (
                    <div className="player-stats-dropdown">
                      {seasonStats.map((stats, idx) => (
                        <div
                          key={`${stats.league_id}-${stats.season_year}`}
                          className={`stats-dropdown-item ${selectedSeasonStats === stats ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedSeasonStats(stats)
                            setShowLeagueDropdown(false)
                          }}
                        >
                          {stats.team_crest && (
                            <img src={stats.team_crest} alt="" className="stats-dropdown-crest" />
                          )}
                          <span>{stats.league_name} {stats.season}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stats Grid - Main stats */}
                  <div className="player-stats-card">
                    <div className="player-stats-grid-main">
                      <div className="stats-grid-item">
                        <span className="stats-grid-value">{selectedSeasonStats.goals}</span>
                        <span className="stats-grid-label">Goals</span>
                      </div>
                      <div className="stats-grid-item">
                        <span className="stats-grid-value">{selectedSeasonStats.assists}</span>
                        <span className="stats-grid-label">Assists</span>
                      </div>
                      <div className="stats-grid-item">
                        <span className="stats-grid-value rating-badge">
                          {selectedSeasonStats.rating?.toFixed(2) || '—'}
                        </span>
                        <span className="stats-grid-label">Rating</span>
                      </div>
                      <div className="stats-grid-item">
                        <span className="stats-grid-value">{selectedSeasonStats.matches}</span>
                        <span className="stats-grid-label">Matches</span>
                      </div>
                      <div className="stats-grid-item">
                        <span className="stats-grid-value">{selectedSeasonStats.started}</span>
                        <span className="stats-grid-label">Started</span>
                      </div>
                      <div className="stats-grid-item">
                        <span className="stats-grid-value">{selectedSeasonStats.minutes}</span>
                        <span className="stats-grid-label">Minutes played</span>
                      </div>
                    </div>
                  </div>

                  {/* Shooting Stats */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Shooting</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow label="Shots" value={selectedSeasonStats.shots} />
                      <StatsDetailRow label="Shots on target" value={selectedSeasonStats.shots_on_target} />
                      <StatsDetailRow
                        label="Shot accuracy"
                        value={selectedSeasonStats.shots > 0
                          ? `${Math.round((selectedSeasonStats.shots_on_target / selectedSeasonStats.shots) * 100)}%`
                          : '—'
                        }
                      />
                      <StatsDetailRow label="Goals" value={selectedSeasonStats.goals} />
                    </div>
                  </div>

                  {/* Passing Stats */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Passing</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow label="Passes" value={selectedSeasonStats.passes} />
                      <StatsDetailRow label="Key passes" value={selectedSeasonStats.key_passes} />
                      <StatsDetailRow
                        label="Pass accuracy"
                        value={selectedSeasonStats.pass_accuracy ? `${selectedSeasonStats.pass_accuracy}%` : '—'}
                      />
                      <StatsDetailRow label="Assists" value={selectedSeasonStats.assists} />
                    </div>
                  </div>

                  {/* Defence Stats */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Defence</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow label="Tackles" value={selectedSeasonStats.tackles} />
                      <StatsDetailRow label="Interceptions" value={selectedSeasonStats.interceptions} />
                      <StatsDetailRow label="Blocks" value={selectedSeasonStats.blocks} />
                      <StatsDetailRow label="Clearances" value={selectedSeasonStats.clearances} />
                    </div>
                  </div>

                  {/* Duels Stats */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Duels</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow
                        label="Ground duels won"
                        value={selectedSeasonStats.duels_total > 0
                          ? `${selectedSeasonStats.duels_won}/${selectedSeasonStats.duels_total} (${Math.round((selectedSeasonStats.duels_won / selectedSeasonStats.duels_total) * 100)}%)`
                          : '—'
                        }
                      />
                      <StatsDetailRow
                        label="Aerial duels won"
                        value={selectedSeasonStats.aerials_total > 0
                          ? `${selectedSeasonStats.aerials_won}/${selectedSeasonStats.aerials_total} (${Math.round((selectedSeasonStats.aerials_won / selectedSeasonStats.aerials_total) * 100)}%)`
                          : '—'
                        }
                      />
                      <StatsDetailRow
                        label="Dribbles completed"
                        value={selectedSeasonStats.dribbles_attempts > 0
                          ? `${selectedSeasonStats.dribbles_success}/${selectedSeasonStats.dribbles_attempts}`
                          : '—'
                        }
                      />
                      <StatsDetailRow label="Times dribbled past" value={selectedSeasonStats.dribbles_past} />
                    </div>
                  </div>

                  {/* Discipline Stats */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Discipline</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow label="Yellow cards" value={selectedSeasonStats.yellows} />
                      <StatsDetailRow label="Second yellow" value={selectedSeasonStats.yellow_red} />
                      <StatsDetailRow label="Red cards" value={selectedSeasonStats.reds} />
                      <StatsDetailRow label="Fouls committed" value={selectedSeasonStats.fouls_committed} />
                      <StatsDetailRow label="Fouls drawn" value={selectedSeasonStats.fouls_drawn} />
                    </div>
                  </div>

                  {/* Penalties - only show if player has penalty involvement */}
                  {(selectedSeasonStats.penalty_won > 0 || selectedSeasonStats.penalty_scored > 0 ||
                    selectedSeasonStats.penalty_missed > 0 || selectedSeasonStats.penalty_committed > 0) && (
                    <div className="player-stats-card">
                      <h3 className="stats-section-title">Penalties</h3>
                      <div className="stats-detail-list">
                        <StatsDetailRow label="Penalties won" value={selectedSeasonStats.penalty_won} />
                        <StatsDetailRow label="Penalties scored" value={selectedSeasonStats.penalty_scored} />
                        <StatsDetailRow label="Penalties missed" value={selectedSeasonStats.penalty_missed} />
                        <StatsDetailRow label="Penalties committed" value={selectedSeasonStats.penalty_committed} />
                      </div>
                    </div>
                  )}

                  {/* Appearances breakdown */}
                  <div className="player-stats-card">
                    <h3 className="stats-section-title">Appearances</h3>
                    <div className="stats-detail-list">
                      <StatsDetailRow label="Started" value={selectedSeasonStats.started} />
                      <StatsDetailRow label="Subbed on" value={selectedSeasonStats.sub_in} />
                      <StatsDetailRow label="Subbed off" value={selectedSeasonStats.sub_out} />
                      <StatsDetailRow label="Unused substitute" value={selectedSeasonStats.bench} />
                      {selectedSeasonStats.captain > 0 && (
                        <StatsDetailRow label="Games as captain" value={selectedSeasonStats.captain} />
                      )}
                    </div>
                  </div>

                  {/* GK Stats - only show if relevant */}
                  {(selectedSeasonStats.saves > 0 || selectedSeasonStats.goals_conceded > 0) && (
                    <div className="player-stats-card">
                      <h3 className="stats-section-title">Goalkeeping</h3>
                      <div className="stats-detail-list">
                        <StatsDetailRow label="Saves" value={selectedSeasonStats.saves} />
                        <StatsDetailRow label="Goals conceded" value={selectedSeasonStats.goals_conceded} />
                        <StatsDetailRow label="Penalties saved" value={selectedSeasonStats.penalty_saved} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Career Tab */}
          {activeTab === 'Career' && !loading && (
            <div className="player-career-tab">
              {careerLoading && (
                <div className="player-career-loading">
                  <div className="skeleton-card" style={{ height: 50 }} />
                  <div className="skeleton-card" style={{ height: 300 }} />
                </div>
              )}
              {!careerLoading && clubSeasons.length === 0 && careerClubs.length === 0 && nationalSeasons.length === 0 && (
                <div className="player-profile-placeholder">
                  <p>No career data available</p>
                </div>
              )}
              {!careerLoading && (clubSeasons.length > 0 || careerClubs.length > 0 || nationalSeasons.length > 0) && (
                <>
                  {/* View Toggle */}
                  <div className="career-view-toggle">
                    <button
                      className={`career-toggle-btn ${careerView === 'Seasons' ? 'active' : ''}`}
                      onClick={() => setCareerView('Seasons')}
                    >
                      Seasons
                    </button>
                    <button
                      className={`career-toggle-btn ${careerView === 'Club' ? 'active' : ''}`}
                      onClick={() => setCareerView('Club')}
                    >
                      Club
                    </button>
                  </div>

                  {/* Seasons View */}
                  {careerView === 'Seasons' && (
                    <>
                      {/* Club Career Seasons */}
                      {clubSeasons.length > 0 && (
                        <div className="career-seasons-card">
                          <div className="career-section-header">
                            <h3>Club career</h3>
                            <div className="career-stat-icons">
                              <span className="career-icon-label" title="Matches"><IoEye size={14} /></span>
                              <span className="career-icon-label" title="Goals"><GiSoccerBall size={14} /></span>
                              <span className="career-icon-label" title="Assists"><GiRunningShoe size={14} /></span>
                              <span className="career-icon-label" title="Rating">★</span>
                            </div>
                          </div>
                          <div className="career-seasons-list">
                            {(showAllSeasons ? clubSeasons : clubSeasons.slice(0, INITIAL_SEASONS_LIMIT)).map((season, idx) => (
                              <div key={`club-${season.team_id}-${season.season_year}-${idx}`} className="career-season-row">
                                <div className="career-season-team">
                                  {season.team_crest && (
                                    <img src={season.team_crest} alt="" className="career-team-crest" />
                                  )}
                                  <div className="career-team-info">
                                    <span className="career-team-name">{season.team_name}</span>
                                    <span className="career-season-label">{season.season}</span>
                                  </div>
                                </div>
                                <div className="career-season-stats">
                                  <span className="career-stat">{season.matches}</span>
                                  <span className="career-stat">{season.goals}</span>
                                  <span className="career-stat">{season.assists}</span>
                                  <span className={`career-stat rating ${getRatingColorClass(season.rating)}`}>
                                    {season.rating?.toFixed(1) || '—'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {!showAllSeasons && clubSeasons.length > INITIAL_SEASONS_LIMIT && (
                            <button
                              className="career-load-more-btn"
                              onClick={() => setShowAllSeasons(true)}
                            >
                              Load more ({clubSeasons.length - INITIAL_SEASONS_LIMIT} more seasons)
                            </button>
                          )}
                        </div>
                      )}

                      {/* National Team Seasons */}
                      {nationalSeasons.length > 0 && (
                        <div className="career-seasons-card national-career">
                          <div className="career-section-header">
                            <h3>International career</h3>
                            <div className="career-stat-icons">
                              <span className="career-icon-label" title="Matches"><IoEye size={14} /></span>
                              <span className="career-icon-label" title="Goals"><GiSoccerBall size={14} /></span>
                              <span className="career-icon-label" title="Assists"><GiRunningShoe size={14} /></span>
                              <span className="career-icon-label" title="Rating">★</span>
                            </div>
                          </div>
                          <div className="career-seasons-list">
                            {(showAllSeasons ? nationalSeasons : nationalSeasons.slice(0, INITIAL_SEASONS_LIMIT)).map((season, idx) => (
                              <div key={`national-${season.team_id}-${season.season_year}-${idx}`} className="career-season-row">
                                <div className="career-season-team">
                                  {season.team_crest && (
                                    <img src={season.team_crest} alt="" className="career-team-crest" />
                                  )}
                                  <div className="career-team-info">
                                    <span className="career-team-name">{season.team_name}</span>
                                    <span className="career-season-label">{season.season}</span>
                                  </div>
                                </div>
                                <div className="career-season-stats">
                                  <span className="career-stat">{season.matches}</span>
                                  <span className="career-stat">{season.goals}</span>
                                  <span className="career-stat">{season.assists}</span>
                                  <span className={`career-stat rating ${getRatingColorClass(season.rating)}`}>
                                    {season.rating?.toFixed(1) || '—'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {!showAllSeasons && nationalSeasons.length > INITIAL_SEASONS_LIMIT && (
                            <button
                              className="career-load-more-btn"
                              onClick={() => setShowAllSeasons(true)}
                            >
                              Load more ({nationalSeasons.length - INITIAL_SEASONS_LIMIT} more seasons)
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Club View */}
                  {careerView === 'Club' && (
                    <>
                      {/* Club History */}
                      {careerClubs.length > 0 && (
                        <div className="career-clubs-card">
                          <div className="career-section-header">
                            <h3>Club career</h3>
                            <div className="career-stat-icons">
                              <span className="career-icon-label" title="Matches"><IoEye size={14} /></span>
                              <span className="career-icon-label" title="Goals"><GiSoccerBall size={14} /></span>
                            </div>
                          </div>
                          <div className="career-clubs-list">
                            {careerClubs.map((club, idx) => (
                              <div key={`club-${club.team_id}-${idx}`} className="career-club-row">
                                <div className="career-club-team">
                                  {club.team_crest && (
                                    <img src={club.team_crest} alt="" className="career-team-crest" />
                                  )}
                                  <div className="career-team-info">
                                    <span className="career-team-name">
                                      {club.team_name}
                                      {club.transfer_type && (
                                        <span className="career-transfer-type"> ({club.transfer_type})</span>
                                      )}
                                    </span>
                                    <span className="career-dates">
                                      {club.start_date ? formatCareerDate(club.start_date) : '?'} - {club.end_date ? formatCareerDate(club.end_date) : 'now'}
                                    </span>
                                  </div>
                                </div>
                                <div className="career-club-stats">
                                  <span className="career-stat">{club.matches}</span>
                                  <span className="career-stat">{club.goals}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* National Team History */}
                      {nationalTeams.length > 0 && (
                        <div className="career-clubs-card national-career">
                          <div className="career-section-header">
                            <h3>International career</h3>
                            <div className="career-stat-icons">
                              <span className="career-icon-label" title="Matches"><IoEye size={14} /></span>
                              <span className="career-icon-label" title="Goals"><GiSoccerBall size={14} /></span>
                            </div>
                          </div>
                          <div className="career-clubs-list">
                            {nationalTeams.map((team, idx) => (
                              <div key={`national-${team.team_id}-${idx}`} className="career-club-row">
                                <div className="career-club-team">
                                  {team.team_crest && (
                                    <img src={team.team_crest} alt="" className="career-team-crest" />
                                  )}
                                  <div className="career-team-info">
                                    <span className="career-team-name">{team.team_name}</span>
                                    <span className="career-dates">
                                      {team.start_date ? formatCareerDate(team.start_date) : '?'} - {team.end_date ? formatCareerDate(team.end_date) : 'now'}
                                    </span>
                                  </div>
                                </div>
                                <div className="career-club-stats">
                                  <span className="career-stat">{team.matches}</span>
                                  <span className="career-stat">{team.goals}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

// Helper: Stats detail row
function StatsDetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stats-detail-row">
      <span className="stats-detail-label">{label}</span>
      <span className="stats-detail-value">{value}</span>
    </div>
  )
}

// Helper: Get rating color class
function getRatingColorClass(rating: number | null | undefined): string {
  if (!rating) return ''
  if (rating >= 8.0) return 'rating-excellent'
  if (rating >= 7.0) return 'rating-good'
  if (rating >= 6.0) return 'rating-average'
  return 'rating-poor'
}

// Helper: Format career date (Jul 2024)
function formatCareerDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

// Mini Pitch Component for Position Visualization
function MiniPitch({
  primaryPosition,
  secondaryPositions = []
}: {
  primaryPosition: string
  secondaryPositions?: string[]
}) {
  const getPositionCoords = (posCode: string): { x: number; y: number } => {
    const positions: Record<string, { x: number; y: number }> = {
      'GK': { x: 50, y: 88 },
      'CB': { x: 50, y: 72 },
      'LB': { x: 18, y: 65 },
      'RB': { x: 82, y: 65 },
      'DEF': { x: 50, y: 72 },
      'DM': { x: 50, y: 55 },
      'CDM': { x: 50, y: 55 },
      'CM': { x: 50, y: 45 },
      'LM': { x: 18, y: 45 },
      'RM': { x: 82, y: 45 },
      'MID': { x: 50, y: 45 },
      'AM': { x: 50, y: 32 },
      'CAM': { x: 50, y: 32 },
      'LW': { x: 22, y: 22 },
      'RW': { x: 78, y: 22 },
      'ST': { x: 50, y: 12 },
      'CF': { x: 50, y: 18 },
      'FWD': { x: 50, y: 15 },
    }
    return positions[posCode] || { x: 50, y: 50 }
  }

  const primaryCoords = getPositionCoords(primaryPosition)

  return (
    <div className="mini-pitch">
      <svg viewBox="0 0 100 100" className="mini-pitch-svg">
        {/* Pitch background */}
        <rect x="0" y="0" width="100" height="100" fill="rgba(30, 60, 40, 0.6)" rx="4" />

        {/* Pitch lines */}
        <rect x="5" y="5" width="90" height="90" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />

        {/* Center circle */}
        <circle cx="50" cy="50" r="12" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.3)" />

        {/* Center line */}
        <line x1="5" y1="50" x2="95" y2="50" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />

        {/* Penalty areas */}
        <rect x="25" y="5" width="50" height="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <rect x="25" y="77" width="50" height="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />

        {/* Goal areas */}
        <rect x="35" y="5" width="30" height="8" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <rect x="35" y="87" width="30" height="8" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />

        {/* Secondary positions */}
        {secondaryPositions.map((pos, idx) => {
          const coords = getPositionCoords(pos)
          return (
            <g key={idx}>
              <circle cx={coords.x} cy={coords.y} r="6" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
              <text x={coords.x} y={coords.y + 1.5} textAnchor="middle" fontSize="4" fill="rgba(255,255,255,0.6)">{pos}</text>
            </g>
          )
        })}

        {/* Primary position */}
        <circle cx={primaryCoords.x} cy={primaryCoords.y} r="8" fill="rgba(255,255,255,0.9)" />
        <text x={primaryCoords.x} y={primaryCoords.y + 2} textAnchor="middle" fontSize="5" fontWeight="600" fill="#1a1a2e">{primaryPosition}</text>
      </svg>
    </div>
  )
}

// Upcoming Fixture Row Component - shows future fixtures
function UpcomingFixtureRow({ fixture, onClick }: { fixture: UpcomingFixture; onClick?: () => void }) {
  const isHome = fixture.player_team_id === fixture.home_team.id

  // Format date like "Sun 1 Mar"
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  }

  // Format time like "15:00"
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="player-match-row upcoming" onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      {/* Date and League */}
      <div className="player-match-header">
        <span className="player-match-date">{formatDate(fixture.kickoff_utc)}</span>
        <span className="player-match-league">{fixture.league_name || 'Match'}</span>
      </div>

      {/* Match Content */}
      <div className="player-match-content">
        {/* Home Team */}
        <div className="player-match-team home">
          <span className="player-match-team-name">{fixture.home_team.name}</span>
          {fixture.home_team.crest_url && (
            <img src={fixture.home_team.crest_url} alt="" className="player-match-team-logo" />
          )}
        </div>

        {/* Time */}
        <div className="player-match-center">
          <span className="player-match-time">{formatTime(fixture.kickoff_utc)}</span>
        </div>

        {/* Away Team */}
        <div className="player-match-team away">
          {fixture.away_team.crest_url && (
            <img src={fixture.away_team.crest_url} alt="" className="player-match-team-logo" />
          )}
          <span className="player-match-team-name">{fixture.away_team.name}</span>
        </div>
      </div>

      {/* Home/Away indicator */}
      <div className="player-match-stats">
        <span className="player-match-venue">{isHome ? 'Home' : 'Away'}</span>
      </div>
    </div>
  )
}

// Player Match Row Component - shows match with player's contribution
function PlayerMatchRow({ match, onClick }: { match: PlayerMatch; onClick?: () => void }) {
  const isHome = match.player_team_id === match.home_team.id
  const isFinished = match.status === 'FT'

  // Format date like "Sun 1 Mar"
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  }

  // Determine result from player's team perspective
  const getResultClass = (): string => {
    if (!isFinished) return ''
    const teamScore = isHome ? (match.home_score ?? 0) : (match.away_score ?? 0)
    const opponentScore = isHome ? (match.away_score ?? 0) : (match.home_score ?? 0)
    if (teamScore > opponentScore) return 'win'
    if (teamScore < opponentScore) return 'loss'
    return 'draw'
  }

  // Get rating color class
  const getRatingClass = (rating: number | null | undefined): string => {
    if (!rating) return ''
    if (rating >= 8.0) return 'rating-excellent'
    if (rating >= 7.0) return 'rating-good'
    if (rating >= 6.0) return 'rating-average'
    return 'rating-poor'
  }

  const resultClass = getResultClass()

  return (
    <div className="player-match-row" onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      {/* Date and League */}
      <div className="player-match-header">
        <span className="player-match-date">{formatDate(match.kickoff_utc)}</span>
        <span className="player-match-league">{match.league_name || 'Match'}</span>
      </div>

      {/* Match Content */}
      <div className="player-match-content">
        {/* Home Team */}
        <div className="player-match-team home">
          <span className="player-match-team-name">{match.home_team.name}</span>
          {match.home_team.crest_url && (
            <img src={match.home_team.crest_url} alt="" className="player-match-team-logo" />
          )}
        </div>

        {/* Score */}
        <div className="player-match-center">
          <span className={`player-match-score ${resultClass}`}>
            {match.home_score} - {match.away_score}
          </span>
        </div>

        {/* Away Team */}
        <div className="player-match-team away">
          {match.away_team.crest_url && (
            <img src={match.away_team.crest_url} alt="" className="player-match-team-logo" />
          )}
          <span className="player-match-team-name">{match.away_team.name}</span>
        </div>
      </div>

      {/* Player Stats Row */}
      <div className="player-match-stats">
        {/* Rating Badge */}
        {match.rating && (
          <div className={`player-match-rating ${getRatingClass(match.rating)}`}>
            {match.rating.toFixed(1)}
          </div>
        )}

        {/* Minutes */}
        {match.minutes !== null && match.minutes !== undefined && (
          <span className="player-match-minutes">{match.minutes}'</span>
        )}

        {/* Goals */}
        {match.goals > 0 && (
          <div className="player-match-contribution goals">
            <IoFootball size={14} />
            <span>{match.goals > 1 ? `×${match.goals}` : ''}</span>
          </div>
        )}

        {/* Assists */}
        {match.assists > 0 && (
          <div className="player-match-contribution assists">
            <GiRunningShoe size={14} />
            <span>{match.assists > 1 ? `×${match.assists}` : ''}</span>
          </div>
        )}

        {/* Cards */}
        {match.yellows > 0 && (
          <div className="player-match-card yellow">
            <IoSquare size={10} />
          </div>
        )}
        {match.reds > 0 && (
          <div className="player-match-card red">
            <IoSquare size={10} />
          </div>
        )}

        {/* Starter indicator */}
        {match.is_starter === false && (
          <span className="player-match-sub">SUB</span>
        )}
      </div>
    </div>
  )
}

// Skeleton Loading Component
function PlayerProfileSkeleton() {
  return (
    <div className="player-profile-skeleton">
      {/* Bio card skeleton - 4 items */}
      <div className="skeleton-card">
        <div className="skeleton-bio-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton-bio-item">
              <div className="skeleton-line" style={{ width: '60%', height: 20 }} />
              <div className="skeleton-line" style={{ width: '40%', height: 12, marginTop: 4 }} />
            </div>
          ))}
        </div>
      </div>

      {/* League stats skeleton */}
      <div className="skeleton-card">
        <div className="skeleton-league-header">
          <div className="skeleton-circle" style={{ width: 24, height: 24 }} />
          <div className="skeleton-line" style={{ width: 120, height: 16 }} />
        </div>
        <div className="skeleton-league-stats">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton-stat">
              <div className="skeleton-line" style={{ width: 40, height: 28 }} />
              <div className="skeleton-line" style={{ width: 50, height: 12, marginTop: 4 }} />
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default PlayerProfilePage
